import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  Timestamp,
  writeBatch,
  where,
} from 'firebase/firestore';
import { db } from '../../sdk_initialization';
import { getVendor } from '../services/vendorsService';
import { getUserProfile } from '../services/profilesService';
import { listEvents } from '../services/eventsService';
import { getLayoutPublic } from '../services/layoutsService';
import { listAttendeesByEventAndVendor } from '../services/attendeesService';
import { getTotalAllowedAttendees } from '../services/attendeeCapacityService';
import { printVendorBadgesWithTemplate } from '../services/zebraPrintService';
import { useAuth } from '../context/AuthContext';
import './ProfilePage.css';

const CHECKINS_COLLECTION = 'checkins';
const ATTENDEES_COLLECTION = 'attendees';
const PRECHECKINS_COLLECTION = 'precheckins';
const BAZAAR_CHECKIN_RETURN_UPDATE_KEY = 'bazaarCheckin:returnUpdate';

const isTruthyConsent = (value) => {
  if (value === true || value === 1) return true;
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'y';
};

const parseConsentStatus = (precheckinData) => {
  const pre = precheckinData || {};
  return {
    signed: isTruthyConsent(pre?.consentSigned)
      || isTruthyConsent(pre?.consentAccepted)
      || isTruthyConsent(pre?.consent?.signed)
      || isTruthyConsent(pre?.consent?.agreed)
      || isTruthyConsent(pre?.consent?.acknowledged),
    fullName: String(pre?.consentFullName || pre?.consent?.fullName || pre?.consent?.printFullName || '').trim(),
    signedDate: String(pre?.consentDate || pre?.consent?.signedDate || '').trim(),
  };
};

const toIsoString = (value) => {
  if (!value) return null;
  try {
    const dateValue = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
    if (Number.isNaN(dateValue.getTime())) return null;
    return dateValue.toISOString();
  } catch {
    return null;
  }
};

const VendorCheckin = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { vendorId } = useParams();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('eventId') || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [vendor, setVendor] = useState(null);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [eventName, setEventName] = useState('');
  const [badgeTemplateName, setBadgeTemplateName] = useState('');
  const [eventFreeAttendeesPerBooth, setEventFreeAttendeesPerBooth] = useState(0);
  const [booths, setBooths] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [existingCheckin, setExistingCheckin] = useState(null);
  const [precheckinDetails, setPrecheckinDetails] = useState(null);
  const [consentStatus, setConsentStatus] = useState({ signed: false, fullName: '', signedDate: '' });
  const [attendeeCapacitySummary, setAttendeeCapacitySummary] = useState({
    freeAllowed: 0,
    purchased: 0,
    current: 0,
    totalAllowed: 0,
  });

  const [checkingIn, setCheckingIn] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkinError, setCheckinError] = useState('');
  const [checkinByName, setCheckinByName] = useState('');
  const [checkoutByName, setCheckoutByName] = useState('');
  const [boothSortBy, setBoothSortBy] = useState('number');
  const [boothSortDir, setBoothSortDir] = useState('asc');
  const [attendeeSortBy, setAttendeeSortBy] = useState('firstName');
  const [attendeeSortDir, setAttendeeSortDir] = useState('asc');
  const [attendeePrintSelection, setAttendeePrintSelection] = useState({});

  const attendeeIdOf = useCallback((attendee) => {
    return String(attendee?.id || '').trim();
  }, []);

  const queueBazaarCheckinReturnUpdate = useCallback((checkinData) => {
    if (!vendorId || !eventId || typeof window === 'undefined' || !window.sessionStorage) return;
    try {
      window.sessionStorage.setItem(BAZAAR_CHECKIN_RETURN_UPDATE_KEY, JSON.stringify({
        eventId,
        vendorId,
        checkinAt: toIsoString(checkinData?.checkinAt),
        checkoutAt: toIsoString(checkinData?.checkoutAt),
      }));
    } catch {
      // Ignore return update cache errors.
    }
  }, [vendorId, eventId]);

  const applyAttendeeUpdates = useCallback(async (updater) => {
    const rows = Array.isArray(attendees) ? attendees : [];
    const batch = writeBatch(db);
    const now = Timestamp.now();
    let changed = false;

    const nextRows = rows.map((a) => {
      const attendeeId = attendeeIdOf(a);
      if (!attendeeId) return a;

      const patch = updater(a, attendeeId);
      if (!patch || Object.keys(patch).length === 0) return a;

      changed = true;
      batch.set(
        doc(db, ATTENDEES_COLLECTION, attendeeId),
        {
          ...patch,
          updatedAt: now,
        },
        { merge: true }
      );
      return {
        ...a,
        ...patch,
        updatedAt: now,
      };
    });

    if (changed) {
      await batch.commit();
      setAttendees(nextRows);
    }

    return nextRows;
  }, [attendees, attendeeIdOf]);

  const syncAttendeeCheckedInForVendor = useCallback(async (nextChecked) => {
    if (!vendorId || !eventId) return [];

    const rows = await listAttendeesByEventAndVendor(eventId, vendorId).catch(() => []);
    const attendeeRows = Array.isArray(rows) ? rows : [];
    const batch = writeBatch(db);
    const now = Timestamp.now();
    let changed = false;

    const nextRows = attendeeRows.map((a) => {
      const attendeeId = attendeeIdOf(a);
      if (!attendeeId) return a;

      if (a?.checkedIn === nextChecked) {
        return a;
      }

      changed = true;
      batch.set(
        doc(db, ATTENDEES_COLLECTION, attendeeId),
        {
          checkedIn: nextChecked,
          updatedAt: now,
        },
        { merge: true }
      );
      return {
        ...a,
        checkedIn: nextChecked,
        updatedAt: now,
      };
    });

    if (changed) {
      await batch.commit();
    }

    setAttendees(nextRows);
    return nextRows;
  }, [vendorId, eventId, attendeeIdOf]);

  useEffect(() => {
    if (!vendorId || !eventId) {
      setError('Missing vendor or event information.');
      setLoading(false);
      return;
    }

    let cancelled = false;
    let unsubscribeReservations = null;

    (async () => {
      try {
        setLoading(true);
        setError('');

        // Load vendor + contact in parallel
        const vendorData = await getVendor(vendorId).catch(() => null);
        if (!cancelled) setVendor(vendorData);

        const vendorAdminUid = String(vendorData?.vendorAdmin || '').trim();
        const memberUids = Array.isArray(vendorData?.memberUids) ? vendorData.memberUids : [];
        const fallbackUid = memberUids.find(uid => String(uid || '').trim()) || '';
        const contactUid = vendorAdminUid || String(fallbackUid || '').trim();
        if (!contactUid && !cancelled) {
          setContactName('');
          setContactEmail('');
          setContactPhone('');
        }
        if (contactUid) {
          const cp = await getUserProfile(contactUid).catch(() => null);
          if (!cancelled) {
            setContactName(
              `${cp?.firstName || ''} ${cp?.lastName || ''}`.trim() || cp?.name || cp?.email || ''
            );
            setContactEmail(cp?.email || '');
            setContactPhone(cp?.phone || cp?.phoneNumber || '');
          }
        }

        // Load event name + layout cells
        const events = await listEvents().catch(() => []);
        const event = (events || []).find(ev => ev.id === eventId);
        if (!cancelled) setEventName(event?.name || eventId);
        if (!cancelled) setBadgeTemplateName(String(event?.badgeTemplateName || '').trim());
        if (!cancelled) setEventFreeAttendeesPerBooth(Number(event?.freeAttendeesPerBooth || 0));

        const cellMap = new Map(); // boothId -> cell
        if (event?.layoutId) {
          try {
            const layout = await getLayoutPublic(event.layoutId);
            (Array.isArray(layout?.cellData) ? layout.cellData : []).forEach(c => {
              if (c?.id) cellMap.set(c.id, c);
            });
          } catch {
            // Non-fatal — booth level/type will fall back to reservation fields
          }
        }

        // Load existing check-in record
        const checkinRef = doc(db, CHECKINS_COLLECTION, `${eventId}_${vendorId}`);
        const checkinSnap = await getDoc(checkinRef).catch(() => null);
        if (!cancelled && checkinSnap?.exists()) {
          setExistingCheckin({ id: checkinSnap.id, ...checkinSnap.data() });
        }

        // Load pre-checkin consent status for check-in gating and display.
        // Support both doc-id formats and a field query fallback for legacy records.
        let precheckinData = null;
        const precheckinDocIds = [`${eventId}_${vendorId}`, `${vendorId}_${eventId}`];
        for (const precheckinId of precheckinDocIds) {
          if (precheckinData) break;
          const snap = await getDoc(doc(db, PRECHECKINS_COLLECTION, precheckinId)).catch(() => null);
          if (snap?.exists()) {
            precheckinData = snap.data() || {};
          }
        }

        if (!precheckinData) {
          const fallbackQuery = query(
            collection(db, PRECHECKINS_COLLECTION),
            where('eventId', '==', String(eventId || '').trim()),
            where('vendorId', '==', String(vendorId || '').trim())
          );
          const fallbackSnap = await getDocs(fallbackQuery).catch(() => null);
          if (fallbackSnap && !fallbackSnap.empty) {
            precheckinData = fallbackSnap.docs[0]?.data() || {};
          }
        }

        if (!cancelled) {
          if (precheckinData) {
            setPrecheckinDetails(precheckinData);
            setConsentStatus(parseConsentStatus(precheckinData));
          } else {
            setPrecheckinDetails(null);
            setConsentStatus({ signed: false, fullName: '', signedDate: '' });
          }
        }

        // Load attendees (one-time)
        const attendeeDocs = await listAttendeesByEventAndVendor(eventId, vendorId).catch(() => []);
        if (!cancelled) setAttendees(attendeeDocs || []);

        // Live reservation subscription
        const resQuery = query(
          collection(db, 'boothReservations'),
          where('eventId', '==', eventId),
          where('vendorId', '==', vendorId),
          where('status', '==', 'Reserved')
        );

        unsubscribeReservations = onSnapshot(
          resQuery,
          (snapshot) => {
            if (cancelled) return;
            const boothList = snapshot.docs.map(d => {
              const data = d.data();
              const boothId = data.boothId || d.id;
              const cell = cellMap.get(boothId);
              const isCorner = cell ? (cell.corner === 'Y' || cell.corner === true) : false;
              return {
                id: boothId,
                number: cell?.number || data.boothNumber || data.number || boothId,
                level: cell?.level || data.level || '',
                boothType: isCorner ? 'Corner' : 'Inline',
              };
            });
            boothList.sort((a, b) =>
              String(a.number).localeCompare(String(b.number), undefined, {
                numeric: true,
                sensitivity: 'base',
              })
            );
            setBooths(boothList);
            setLoading(false);
          },
          (err) => {
            if (!cancelled) {
              setError(err?.message || 'Failed to load booth reservations.');
              setLoading(false);
            }
          }
        );
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Failed to load vendor check-in data.');
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      try {
        if (typeof unsubscribeReservations === 'function') unsubscribeReservations();
      } catch {
        // Ignore cleanup errors
      }
    };
  }, [vendorId, eventId]);

  useEffect(() => {
    if (!vendorId || !eventId) return;

    let cancelled = false;
    (async () => {
      const freePerBooth = Number(eventFreeAttendeesPerBooth || 0);
      const boothCount = Array.isArray(booths) ? booths.length : 0;
      const currentCount = Array.isArray(attendees) ? attendees.length : 0;

      try {
        const totals = await getTotalAllowedAttendees(vendorId, eventId, boothCount, freePerBooth);
        if (!cancelled) {
          setAttendeeCapacitySummary({
            freeAllowed: Number(totals?.freeAttendees || 0),
            purchased: Number(totals?.purchasedAttendees || 0),
            current: currentCount,
            totalAllowed: Number(totals?.totalAllowed || 0),
          });
        }
      } catch {
        if (!cancelled) {
          const freeAllowed = boothCount * freePerBooth;
          setAttendeeCapacitySummary({
            freeAllowed,
            purchased: 0,
            current: currentCount,
            totalAllowed: freeAllowed,
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [vendorId, eventId, booths, attendees, eventFreeAttendeesPerBooth]);

  const handleCheckinToggle = useCallback(async (nextChecked) => {
    if (!vendorId || !eventId) return;
    if (nextChecked && !consentStatus.signed) {
      window.alert('Vendor must sign the consent form first before check-in.');
      return;
    }
    if (nextChecked) {
      if (!window.confirm(`Confirm check-in for ${vendor?.name || 'this vendor'}? All attendees will be checked in together.`)) return;
    } else {
      if (!window.confirm(`Undo check-in for ${vendor?.name || 'this vendor'}? The record will be kept.`)) return;
    }

    try {
      setCheckingIn(true);
      setCheckinError('');

      await syncAttendeeCheckedInForVendor(nextChecked);

      const checkinData = {
        vendorId,
        eventId,
        checkedIn: nextChecked,
        checkedOut: false,
        createdAt: existingCheckin?.createdAt || Timestamp.now(),
        checkinAt: nextChecked ? Timestamp.now() : null,
        checkoutAt: null,
        checkinByUid: nextChecked ? (currentUser?.uid || null) : null,
        checkoutByUid: null,
        updatedAt: Timestamp.now(),
        booths: deleteField(),
        attendees: deleteField(),
      };

      const checkinRef = doc(db, CHECKINS_COLLECTION, `${eventId}_${vendorId}`);
      await setDoc(checkinRef, checkinData, { merge: true });
      setExistingCheckin(prev => ({
        ...(prev || { id: `${eventId}_${vendorId}` }),
        ...checkinData,
      }));
      queueBazaarCheckinReturnUpdate(checkinData);
    } catch (e) {
      setCheckinError(e?.message || 'Failed to complete check-in.');
    } finally {
      setCheckingIn(false);
    }
  }, [vendorId, eventId, vendor, consentStatus.signed, existingCheckin?.createdAt, syncAttendeeCheckedInForVendor, currentUser?.uid, queueBazaarCheckinReturnUpdate]);

  const handlePrint = async () => {
    const selected = attendeesWithMeta.filter(a => a.attendeeId && attendeePrintSelection[a.attendeeId] !== false);
    if (selected.length === 0) {
      window.alert('Select at least one attendee to print badges.');
      return;
    }

    const selectedAttendeeIds = new Set(selected.map(a => a.attendeeId));

    const updateData = {
      vendorId,
      eventId,
      checkedIn: existingCheckin?.checkedIn === true || false,
      checkedOut: existingCheckin?.checkedOut === true || false,
      createdAt: existingCheckin?.createdAt || Timestamp.now(),
      checkinAt: existingCheckin?.checkinAt || null,
      checkoutAt: existingCheckin?.checkoutAt || null,
      checkinByUid: existingCheckin?.checkinByUid || null,
      checkoutByUid: existingCheckin?.checkoutByUid || null,
      updatedAt: Timestamp.now(),
      booths: deleteField(),
      attendees: deleteField(),
    };

    try {
      await printVendorBadgesWithTemplate(
        selected,
        vendor?.name || vendorId || 'Vendor',
        badgeTemplateName || 'vendor-badge-template'
      );

      const printedSuccessfully = window.confirm(
        `Did ${selected.length} badge(s) print successfully? Click OK only if printing completed.`
      );

      if (printedSuccessfully) {
        await applyAttendeeUpdates((a, attendeeId) => {
          if (!selectedAttendeeIds.has(attendeeId)) return null;
          const current = Math.max(0, Number(a?.badgeCount || 0));
          return { badgeCount: current + 1 };
        });

        const checkinRef = doc(db, CHECKINS_COLLECTION, `${eventId}_${vendorId}`);
        await setDoc(checkinRef, updateData, { merge: true });
        setExistingCheckin(prev => ({ ...(prev || { id: `${eventId}_${vendorId}` }), ...updateData }));
      }
    } catch (e) {
      window.alert(e?.message || 'Failed to print badges.');
    }
  };

  const handleCheckoutToggle = useCallback(async (nextChecked) => {
    if (!vendorId || !eventId) return;
    const checkedInNow = existingCheckin?.checkedIn === true || (existingCheckin?.checkedIn == null && Boolean(existingCheckin?.checkinAt));
    if (!checkedInNow) return;
    if (nextChecked) {
      if (!window.confirm(`Confirm checkout for ${vendor?.name || 'this vendor'}?`)) return;
    } else {
      if (!window.confirm(`Undo checkout for ${vendor?.name || 'this vendor'}? The record will be kept.`)) return;
    }

    try {
      setCheckingOut(true);
      setCheckinError('');

      const checkinRef = doc(db, CHECKINS_COLLECTION, `${eventId}_${vendorId}`);
      const updateData = {
        vendorId,
        eventId,
        checkedIn: true,
        checkedOut: nextChecked,
        createdAt: existingCheckin?.createdAt || Timestamp.now(),
        checkinAt: existingCheckin?.checkinAt || Timestamp.now(),
        checkoutAt: nextChecked ? Timestamp.now() : null,
        checkinByUid: existingCheckin?.checkinByUid || currentUser?.uid || null,
        checkoutByUid: nextChecked ? (currentUser?.uid || null) : null,
        updatedAt: Timestamp.now(),
        booths: deleteField(),
        attendees: deleteField(),
      };
      await setDoc(checkinRef, updateData, { merge: true });
      setExistingCheckin(prev => ({
        ...(prev || { id: `${eventId}_${vendorId}` }),
        ...updateData,
      }));
      queueBazaarCheckinReturnUpdate(updateData);
    } catch (e) {
      setCheckinError(e?.message || 'Failed to checkout vendor.');
    } finally {
      setCheckingOut(false);
    }
  }, [vendorId, eventId, vendor, existingCheckin?.checkedIn, existingCheckin?.createdAt, existingCheckin?.checkinAt, existingCheckin?.checkinByUid, currentUser?.uid, queueBazaarCheckinReturnUpdate]);

  const isCheckedIn = existingCheckin?.checkedIn === true || (existingCheckin?.checkedIn == null && Boolean(existingCheckin?.checkinAt));
  const isCheckedOut = existingCheckin?.checkedOut === true || (existingCheckin?.checkedOut == null && Boolean(existingCheckin?.checkoutAt));

  useEffect(() => {
    if (loading || checkingIn || checkingOut || !isCheckedIn || isCheckedOut) return;

    let cancelled = false;
    (async () => {
      try {
        await applyAttendeeUpdates((a) => {
          return a?.checkedIn === true ? null : { checkedIn: true };
        });
      } catch (e) {
        if (!cancelled) {
          setCheckinError(prev => prev || (e?.message || 'Failed to sync attendee check-in status.'));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loading, checkingIn, checkingOut, isCheckedIn, isCheckedOut, applyAttendeeUpdates]);

  const checkinDateStr = (() => {
    const ts = existingCheckin?.checkinAt;
    if (!ts) return null;
    try {
      const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
      return isNaN(d.getTime()) ? null : d.toLocaleString();
    } catch {
      return null;
    }
  })();

  const checkoutDateStr = (() => {
    const ts = existingCheckin?.checkoutAt;
    if (!ts) return null;
    try {
      const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
      return isNaN(d.getTime()) ? null : d.toLocaleString();
    } catch {
      return null;
    }
  })();

  const consentSignedDateStr = (() => {
    const raw = consentStatus?.signedDate;
    if (!raw) return null;
    try {
      const d = new Date(raw);
      if (!Number.isNaN(d.getTime())) return d.toLocaleString();
    } catch {
      // fall through and return raw value
    }
    return raw;
  })();

  const consentMethod = isTruthyConsent(precheckinDetails?.paperConsentForm) ? 'Paper' : 'Electronic';
  const showConsentMethod = Boolean(precheckinDetails);
  const consentMethodTagStyle = {
    display: 'inline-block',
    background: '#ecfdf5',
    border: '1px solid #86efac',
    color: '#166534',
    padding: '4px 10px',
    borderRadius: 999,
    fontWeight: 700,
    fontSize: 12,
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const inUid = String(existingCheckin?.checkinByUid || '').trim();
      const outUid = String(existingCheckin?.checkoutByUid || '').trim();

      if (!inUid) {
        setCheckinByName('');
      } else {
        const p = await getUserProfile(inUid).catch(() => null);
        if (!cancelled) {
          setCheckinByName(`${p?.firstName || ''} ${p?.lastName || ''}`.trim() || p?.name || p?.email || inUid);
        }
      }

      if (!outUid) {
        setCheckoutByName('');
      } else {
        const p = await getUserProfile(outUid).catch(() => null);
        if (!cancelled) {
          setCheckoutByName(`${p?.firstName || ''} ${p?.lastName || ''}`.trim() || p?.name || p?.email || outUid);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [existingCheckin?.checkinByUid, existingCheckin?.checkoutByUid]);

  const backQuery = searchParams.toString();
  const backPath = `/admin/bazaar-checkin${backQuery ? `?${backQuery}` : ''}`;
  const vendorCountryCode = String(vendor?.businessAreaCode || vendor?.countryCode || '').trim();
  const vendorLocalPhone = String(vendor?.phoneNumber || vendor?.phone || '').trim();
  const vendorPhone = [vendorCountryCode, vendorLocalPhone].filter(Boolean).join(' ').trim();

  const getCreatedAtMs = useCallback((value) => {
    if (!value) return 0;
    if (typeof value?.toDate === 'function') return value.toDate().getTime();
    if (typeof value?.seconds === 'number') return value.seconds * 1000;
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);

  const attendeesWithMeta = useMemo(() => {
    return (attendees || []).map((a) => {
      const attendeeId = attendeeIdOf(a);
      return {
        ...a,
        attendeeId,
        checkedIn: a?.checkedIn === true,
        badgeCount: Math.max(0, Number(a?.badgeCount || 0)),
      };
    });
  }, [attendees, attendeeIdOf]);

  const firstDisplayPrimaryIds = useMemo(() => {
    const rows = Array.isArray(attendeesWithMeta) ? attendeesWithMeta : [];
    if (rows.some((a) => a?.isPrimary === true)) return new Set();

    let first = null;
    rows.forEach((a) => {
      if (!a?.attendeeId) return;
      const createdAtMs = getCreatedAtMs(a?.createdAt);
      if (!first || createdAtMs < first.createdAtMs) {
        first = { attendeeId: a.attendeeId, createdAtMs };
      }
    });

    return first ? new Set([first.attendeeId]) : new Set();
  }, [attendeesWithMeta, getCreatedAtMs]);

  useEffect(() => {
    setAttendeePrintSelection((prev) => {
      const next = {};
      attendeesWithMeta.forEach((a) => {
        if (!a.attendeeId) return;
        next[a.attendeeId] = Object.prototype.hasOwnProperty.call(prev, a.attendeeId) ? prev[a.attendeeId] : true;
      });
      return next;
    });
  }, [attendeesWithMeta]);

  const selectableAttendees = useMemo(() => attendeesWithMeta.filter(a => Boolean(a.attendeeId)), [attendeesWithMeta]);
  const allSelected = selectableAttendees.length > 0 && selectableAttendees.every(a => attendeePrintSelection[a.attendeeId] !== false);
  const anySelected = selectableAttendees.some(a => attendeePrintSelection[a.attendeeId] !== false);

  const handleToggleAllPrint = useCallback((nextChecked) => {
    const next = {};
    selectableAttendees.forEach((a) => {
      next[a.attendeeId] = nextChecked;
    });
    setAttendeePrintSelection(next);
  }, [selectableAttendees]);

  const handleToggleOnePrint = useCallback((attendeeId, nextChecked) => {
    if (!attendeeId) return;
    setAttendeePrintSelection(prev => ({ ...prev, [attendeeId]: nextChecked }));
  }, []);

  const handleAddAttendees = useCallback(() => {
    const params = new URLSearchParams();
    const currentQuery = searchParams.toString();
    const returnTo = `/admin/bazaar-checkin/${vendorId}${currentQuery ? `?${currentQuery}` : ''}`;
    if (eventId) params.set('eventId', eventId);
    if (vendorId) params.set('vendorId', vendorId);
    params.set('returnTo', returnTo);
    const query = params.toString();
    navigate(`/attendees${query ? `?${query}` : ''}`);
  }, [navigate, eventId, vendorId, searchParams]);

  const handleOpenConsentForm = useCallback(() => {
    const params = new URLSearchParams();
    const currentQuery = searchParams.toString();
    const returnTo = `/admin/bazaar-checkin/${vendorId}${currentQuery ? `?${currentQuery}` : ''}`;
    if (eventId) params.set('eventId', eventId);
    if (vendorId) params.set('vendorId', vendorId);
    params.set('returnTo', returnTo);
    params.set('source', 'vendor-checkin');
    const query = params.toString();
    navigate(`/pre-checkin-steps${query ? `?${query}` : ''}`);
  }, [navigate, eventId, vendorId, searchParams]);

  const sortedBooths = useMemo(() => {
    const dir = boothSortDir === 'asc' ? 1 : -1;
    const rows = Array.isArray(booths) ? booths.slice() : [];
    rows.sort((a, b) => {
      if (boothSortBy === 'number') {
        return String(a.number || '').localeCompare(String(b.number || ''), undefined, {
          numeric: true,
          sensitivity: 'base',
        }) * dir;
      }
      if (boothSortBy === 'level') {
        return String(a.level || '').localeCompare(String(b.level || ''), undefined, {
          numeric: true,
          sensitivity: 'base',
        }) * dir;
      }
      if (boothSortBy === 'type') {
        return String(a.boothType || '').localeCompare(String(b.boothType || ''), undefined, {
          numeric: true,
          sensitivity: 'base',
        }) * dir;
      }
      return 0;
    });
    return rows;
  }, [booths, boothSortBy, boothSortDir]);

  const sortedAttendees = useMemo(() => {
    const dir = attendeeSortDir === 'asc' ? 1 : -1;
    const rows = Array.isArray(attendeesWithMeta) ? attendeesWithMeta.slice() : [];
    rows.sort((a, b) => {
      if (attendeeSortBy === 'firstName') {
        return String(a.firstName || '').localeCompare(String(b.firstName || ''), undefined, { sensitivity: 'base' }) * dir;
      }
      if (attendeeSortBy === 'lastName') {
        return String(a.lastName || '').localeCompare(String(b.lastName || ''), undefined, { sensitivity: 'base' }) * dir;
      }
      if (attendeeSortBy === 'gender') {
        return String(a.gender || '').localeCompare(String(b.gender || ''), undefined, { sensitivity: 'base' }) * dir;
      }
      if (attendeeSortBy === 'yob') {
        const aYob = Number(a.yob || 0);
        const bYob = Number(b.yob || 0);
        return (aYob - bYob) * dir;
      }
      if (attendeeSortBy === 'badgeCount') {
        return (Number(a.badgeCount || 0) - Number(b.badgeCount || 0)) * dir;
      }
      return 0;
    });
    return rows;
  }, [attendeesWithMeta, attendeeSortBy, attendeeSortDir]);

  const formatDateTime = useCallback((value) => {
    if (!value) return '';
    try {
      const d = typeof value?.toDate === 'function'
        ? value.toDate()
        : (typeof value?.seconds === 'number' ? new Date(value.seconds * 1000) : new Date(value));
      return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
    } catch {
      return String(value || '');
    }
  }, []);

  const precheckinScheduleDate = String(
    precheckinDetails?.selectedCheckinScheduleDate || precheckinDetails?.selectedCheckinSchedule?.date || ''
  ).trim();

  const precheckinRequiredItemsByBooth = useMemo(() => {
    const rows = Array.isArray(precheckinDetails?.requiredItemSelections)
      ? precheckinDetails.requiredItemSelections
      : (Array.isArray(precheckinDetails?.requiredItemsSelections) ? precheckinDetails.requiredItemsSelections : []);

    const normalizedRows = rows
      .map((row, index) => ({
        sequence: Number(row?.sequence ?? (index + 1)),
        name: String(row?.name || row?.itemName || '').trim(),
        boothNumber: String(row?.boothNumber || row?.booth || '').trim(),
      }))
      .filter(row => row.name);

    const boothGroups = new Map();
    normalizedRows.forEach((row) => {
      const boothNumber = String(row.boothNumber || '').trim();
      const key = boothNumber || '__unspecified__';
      if (!boothGroups.has(key)) {
        boothGroups.set(key, {
          boothNumber: boothNumber || 'Unspecified booth',
          items: [],
        });
      }
      boothGroups.get(key).items.push({
        sequence: row.sequence,
        name: row.name,
      });
    });

    const parseBoothSortNumber = (value) => {
      const text = String(value || '').trim();
      const match = text.match(/\d+/);
      if (!match) return Number.POSITIVE_INFINITY;
      const n = Number(match[0]);
      return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
    };

    return Array.from(boothGroups.values())
      .map(group => ({
        ...group,
        items: group.items.sort((a, b) => {
          if (a.sequence !== b.sequence) return a.sequence - b.sequence;
          return String(a.name || '').localeCompare(String(b.name || ''));
        }),
      }))
      .sort((a, b) => {
        const an = parseBoothSortNumber(a.boothNumber);
        const bn = parseBoothSortNumber(b.boothNumber);
        if (an !== bn) return an - bn;
        return String(a.boothNumber || '').localeCompare(String(b.boothNumber || ''), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      });
  }, [precheckinDetails]);

  const precheckinCreatedAtStr = formatDateTime(precheckinDetails?.createdAt);
  const precheckinUpdatedAtStr = formatDateTime(precheckinDetails?.updatedAt);

  return (
    <div className="profile-container profile-wide-container">
      {/* Header row */}
      <div className="profile-header-row">
        <button type="button" className="back-btn" onClick={() => navigate(backPath)}>
          ← Back
        </button>
        <div style={{ flex: 1 }} />
        <button type="button" className="cancel-btn" onClick={() => navigate(backPath)}>
          Close
        </button>
      </div>

      <h2 className="profile-title" style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span>Vendor Check-In</span>
        <span style={{ fontSize: '0.72em', fontWeight: 500, color: '#555' }}>
          for {eventName || eventId || 'Event'}
        </span>
      </h2>

      {error && <div className="profile-error">{error}</div>}
      {loading && <div className="profile-hint">Loading vendor data…</div>}

      {!loading && !error && (
        <>
          {/* Vendor summary card */}
          <div
            style={{
              background: '#f8f9fa',
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              padding: 16,
              marginBottom: 20,
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 12,
              }}
            >
              <div>
                <div className="profile-label">Vendor</div>
                <div style={{ fontWeight: 600, fontSize: 16 }}>{vendor?.name || vendorId}</div>
              </div>
              <div>
                <div className="profile-label">Vendor Number</div>
                <div>{vendor?.vendorNumber || '—'}</div>
              </div>
              <div>
                <div className="profile-label">Type</div>
                <div>{vendor?.type || '—'}</div>
              </div>
              <div>
                <div className="profile-label">Vendor Email</div>
                <div>{vendor?.email || '—'}</div>
              </div>
              <div>
                <div className="profile-label">Vendor Phone</div>
                <div>{vendorPhone || '—'}</div>
              </div>
              <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #e5e5e5' }} />
              <div>
                <div className="profile-label">Contact</div>
                <div>{contactName || '—'}</div>
              </div>
              <div>
                <div className="profile-label">Contact Email</div>
                <div>{contactEmail || '—'}</div>
              </div>
              <div>
                <div className="profile-label">Contact Phone</div>
                <div>{contactPhone || '—'}</div>
              </div>
            </div>
          </div>

          {/* Check-in / Checkout toggles */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 10 }}>
              {consentStatus.signed ? (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div
                    style={{
                      background: '#ecfdf5',
                      border: '1px solid #86efac',
                      color: '#166534',
                      padding: '8px 12px',
                      borderRadius: 10,
                      fontWeight: 600,
                      fontSize: 14,
                      display: 'inline-block',
                    }}
                  >
                    Consent signed by {consentStatus.fullName || 'Unknown'}{consentSignedDateStr ? ` on ${consentSignedDateStr}` : ''}
                  </div>
                  {showConsentMethod && (
                    <div
                      style={consentMethodTagStyle}
                    >
                      Consent Method: {consentMethod}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div
                    style={{
                      background: '#fff7ed',
                      border: '1px solid #fdba74',
                      color: '#9a3412',
                      padding: '8px 12px',
                      borderRadius: 10,
                      fontWeight: 600,
                      fontSize: 14,
                      display: 'inline-block',
                    }}
                  >
                    Consent form has not been signed.
                  </div>
                  {showConsentMethod && (
                    <div
                      style={consentMethodTagStyle}
                    >
                      Consent Method: {consentMethod}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 28, flexWrap: 'wrap' }}>
              <button
                type="button"
                role="switch"
                aria-checked={isCheckedIn}
                onClick={() => handleCheckinToggle(!isCheckedIn)}
                disabled={checkingIn || checkingOut || (!isCheckedIn && (booths.length === 0 && attendees.length === 0))}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  border: '1px solid #d8dee7',
                  background: isCheckedIn ? '#eaf8ef' : '#f8fafc',
                  padding: '6px 10px',
                  borderRadius: 999,
                  cursor: (checkingIn || checkingOut || (!isCheckedIn && (booths.length === 0 && attendees.length === 0))) ? 'not-allowed' : 'pointer',
                  fontWeight: 700,
                  fontSize: 14,
                  color: '#1f2937',
                }}
              >
                <span
                  style={{
                    width: 34,
                    height: 20,
                    borderRadius: 999,
                    background: isCheckedIn ? '#2fbf71' : '#c9ced6',
                    position: 'relative',
                    transition: 'background 160ms ease',
                    opacity: (checkingIn || checkingOut || (!isCheckedIn && (booths.length === 0 && attendees.length === 0))) ? 0.7 : 1,
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: 2,
                      left: isCheckedIn ? 16 : 2,
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      background: '#fff',
                      transition: 'left 160ms ease',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.25)',
                    }}
                  />
                </span>
                <span style={{ color: '#1f2937' }}>Check In</span>
              </button>

              {(isCheckedIn || isCheckedOut) && (
                <button
                  type="button"
                  role="switch"
                  aria-checked={isCheckedOut}
                  onClick={() => handleCheckoutToggle(!isCheckedOut)}
                  disabled={checkingIn || checkingOut || (!isCheckedIn && !isCheckedOut)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    border: '1px solid #e2d8cd',
                    background: isCheckedOut ? '#fff4ea' : '#f8fafc',
                    padding: '6px 10px',
                    borderRadius: 999,
                    cursor: (checkingIn || checkingOut || (!isCheckedIn && !isCheckedOut)) ? 'not-allowed' : 'pointer',
                    fontWeight: 700,
                    fontSize: 14,
                    color: '#1f2937',
                  }}
                >
                  <span
                    style={{
                      width: 34,
                      height: 20,
                      borderRadius: 999,
                      background: isCheckedOut ? '#f4a259' : '#c9ced6',
                      position: 'relative',
                      transition: 'background 160ms ease',
                      opacity: (checkingIn || checkingOut || (!isCheckedIn && !isCheckedOut)) ? 0.7 : 1,
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        top: 2,
                        left: isCheckedOut ? 16 : 2,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        background: '#fff',
                        transition: 'left 160ms ease',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.25)',
                      }}
                    />
                  </span>
                  <span style={{ color: '#1f2937' }}>Checkout</span>
                </button>
              )}
            </div>

            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {checkinDateStr && (
                <div
                  style={{
                    background: '#eef6ff',
                    color: '#0c4a8a',
                    fontWeight: 600,
                    padding: '6px 12px',
                    borderRadius: 16,
                    fontSize: 14,
                    display: 'inline-block',
                    width: 'fit-content',
                  }}
                >
                  Check-in time: {checkinDateStr}{checkinByName ? ` by ${checkinByName}` : ''}
                </div>
              )}
              {checkoutDateStr && (
                <div
                  style={{
                    background: '#fff3e8',
                    color: '#8a4b08',
                    fontWeight: 600,
                    padding: '6px 12px',
                    borderRadius: 16,
                    fontSize: 14,
                    display: 'inline-block',
                    width: 'fit-content',
                  }}
                >
                  Checkout time: {checkoutDateStr}{checkoutByName ? ` by ${checkoutByName}` : ''}
                </div>
              )}
            </div>
          </div>

          {checkinError && (
            <div className="profile-error" style={{ marginBottom: 12 }}>
              {checkinError}
            </div>
          )}

          {/* Reserved Booths */}
          <h3 style={{ marginBottom: 8 }}>Reserved Booths ({booths.length})</h3>
          {booths.length === 0 ? (
            <div className="profile-hint" style={{ marginBottom: 24 }}>
              No reserved booths found for this event.
            </div>
          ) : (
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                marginBottom: 28,
                fontSize: 14,
              }}
            >
              <thead>
                <tr style={{ background: '#f0f0f0' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>
                    <span
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => {
                        setBoothSortBy('number');
                        setBoothSortDir(boothSortBy === 'number' ? (boothSortDir === 'asc' ? 'desc' : 'asc') : 'asc');
                      }}
                    >
                      Booth # {boothSortBy === 'number' ? (boothSortDir === 'asc' ? '▲' : '▼') : ''}
                    </span>
                  </th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>
                    <span
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => {
                        setBoothSortBy('level');
                        setBoothSortDir(boothSortBy === 'level' ? (boothSortDir === 'asc' ? 'desc' : 'asc') : 'asc');
                      }}
                    >
                      Level {boothSortBy === 'level' ? (boothSortDir === 'asc' ? '▲' : '▼') : ''}
                    </span>
                  </th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>
                    <span
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => {
                        setBoothSortBy('type');
                        setBoothSortDir(boothSortBy === 'type' ? (boothSortDir === 'asc' ? 'desc' : 'asc') : 'asc');
                      }}
                    >
                      Type {boothSortBy === 'type' ? (boothSortDir === 'asc' ? '▲' : '▼') : ''}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedBooths.map((b, i) => (
                  <tr
                    key={b.id}
                    style={{
                      borderTop: '1px solid #eee',
                      background: i % 2 === 0 ? '#fff' : '#fafafa',
                    }}
                  >
                    <td style={{ padding: '8px 12px' }}>{b.number}</td>
                    <td style={{ padding: '8px 12px' }}>{b.level || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>{b.boothType}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Attendees */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8, gap: 8 }}>
            <button
              type="button"
              className="apply-btn"
              onClick={handleOpenConsentForm}
              disabled={checkingIn || checkingOut || isCheckedOut}
            >
              Consent Form
            </button>
            <button
              type="button"
              className="apply-btn"
              onClick={handleAddAttendees}
              disabled={checkingIn || checkingOut || isCheckedOut}
            >
              Add/Edit Attendees
            </button>
            <button
              type="button"
              className="apply-btn"
              onClick={handlePrint}
              disabled={!isCheckedIn || isCheckedOut || !anySelected}
            >
              Print Badges
            </button>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 12,
              marginBottom: 12,
            }}
          >
            <div style={{ padding: 12, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: '#1e40af', marginBottom: 4, fontWeight: 700 }}>Free Allowed</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#1e40af' }}>{attendeeCapacitySummary.freeAllowed}</div>
            </div>
            <div style={{ padding: 12, background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: '#7c3aed', marginBottom: 4, fontWeight: 700 }}>Purchased</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#7c3aed' }}>{attendeeCapacitySummary.purchased}</div>
            </div>
            <div style={{ padding: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: '#15803d', marginBottom: 4, fontWeight: 700 }}>Current</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#15803d' }}>{attendeeCapacitySummary.current}</div>
            </div>
            <div style={{ padding: 12, background: '#fefce8', border: '1px solid #fde68a', borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: '#a16207', marginBottom: 4, fontWeight: 700 }}>Total Allowed</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#a16207' }}>{attendeeCapacitySummary.totalAllowed}</div>
            </div>
          </div>
          <h3 style={{ marginBottom: 8 }}>Attendees ({attendees.length})</h3>
          {attendees.length === 0 ? (
            <div className="profile-hint" style={{ marginBottom: 24 }}>
              No attendees registered for this vendor at this event.
            </div>
          ) : (
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                marginBottom: 24,
                fontSize: 14,
              }}
            >
              <thead>
                <tr style={{ background: '#f0f0f0' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => handleToggleAllPrint(e.target.checked)}
                      aria-label="Select all attendees for badge printing"
                    />
                  </th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>
                    <span
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => {
                        setAttendeeSortBy('firstName');
                        setAttendeeSortDir(attendeeSortBy === 'firstName' ? (attendeeSortDir === 'asc' ? 'desc' : 'asc') : 'asc');
                      }}
                    >
                      First Name {attendeeSortBy === 'firstName' ? (attendeeSortDir === 'asc' ? '▲' : '▼') : ''}
                    </span>
                  </th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>
                    <span
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => {
                        setAttendeeSortBy('lastName');
                        setAttendeeSortDir(attendeeSortBy === 'lastName' ? (attendeeSortDir === 'asc' ? 'desc' : 'asc') : 'asc');
                      }}
                    >
                      Last Name {attendeeSortBy === 'lastName' ? (attendeeSortDir === 'asc' ? '▲' : '▼') : ''}
                    </span>
                  </th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>
                    <span
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => {
                        setAttendeeSortBy('gender');
                        setAttendeeSortDir(attendeeSortBy === 'gender' ? (attendeeSortDir === 'asc' ? 'desc' : 'asc') : 'asc');
                      }}
                    >
                      Gender {attendeeSortBy === 'gender' ? (attendeeSortDir === 'asc' ? '▲' : '▼') : ''}
                    </span>
                  </th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>
                    <span
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => {
                        setAttendeeSortBy('yob');
                        setAttendeeSortDir(attendeeSortBy === 'yob' ? (attendeeSortDir === 'asc' ? 'desc' : 'asc') : 'asc');
                      }}
                    >
                      Year of Birth {attendeeSortBy === 'yob' ? (attendeeSortDir === 'asc' ? '▲' : '▼') : ''}
                    </span>
                  </th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>
                    Checked In
                  </th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>
                    <span
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => {
                        setAttendeeSortBy('badgeCount');
                        setAttendeeSortDir(attendeeSortBy === 'badgeCount' ? (attendeeSortDir === 'asc' ? 'desc' : 'asc') : 'asc');
                      }}
                    >
                      Badge Count {attendeeSortBy === 'badgeCount' ? (attendeeSortDir === 'asc' ? '▲' : '▼') : ''}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedAttendees.map((a, i) => (
                  <tr
                    key={a.attendeeId || `attendee-missing-id-${i}`}
                    style={{
                      borderTop: '1px solid #eee',
                      background: i % 2 === 0 ? '#fff' : '#fafafa',
                    }}
                  >
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={a.attendeeId ? attendeePrintSelection[a.attendeeId] !== false : false}
                        disabled={!a.attendeeId}
                        onChange={(e) => handleToggleOnePrint(a.attendeeId, e.target.checked)}
                        aria-label={`Print badge for ${a.firstName || ''} ${a.lastName || ''}`}
                      />
                    </td>
                    <td style={{ padding: '8px 12px' }}>{a.firstName || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      {a.lastName || '—'}
                      {(a?.isPrimary === true || firstDisplayPrimaryIds.has(a.attendeeId)) && (
                        <span
                          title="Primary attendee record"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            marginLeft: 8,
                            padding: '2px 8px',
                            borderRadius: 999,
                            backgroundColor: '#fef3c7',
                            color: '#92400e',
                            fontSize: 11,
                            fontWeight: 600,
                            verticalAlign: 'middle',
                          }}
                        >
                          <span aria-hidden="true">★</span>
                          Primary
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '8px 12px' }}>{a.gender || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>{a.yob || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>{a.checkedIn ? 'Yes' : 'No'}</td>
                    <td style={{ padding: '8px 12px' }}>{Number(a.badgeCount || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h3 style={{ marginBottom: 8 }}>Pre-Checkin Data</h3>
          {!precheckinDetails ? (
            <div className="profile-hint" style={{ marginBottom: 24 }}>
              No pre-checkin record found for this vendor and event.
            </div>
          ) : (
            <div
              style={{
                marginBottom: 24,
                border: '1px solid #e2e8f0',
                background: '#f8fafc',
                borderRadius: 10,
                padding: 14,
                fontSize: 14,
                color: '#1f2937',
              }}
            >
              <div style={{ marginBottom: 6 }}><strong>Selected check-in schedule:</strong> {precheckinScheduleDate || '—'}</div>
              <div style={{ marginBottom: 6 }}>
                <strong>Required item selections:</strong>
                {precheckinRequiredItemsByBooth.length === 0 ? (
                  <span> None selected</span>
                ) : (
                  <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {precheckinRequiredItemsByBooth.map((group) => (
                      <div key={`required-booth-${group.boothNumber}`} style={{ padding: '4px 6px', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff' }}>
                        <div style={{ fontWeight: 700, marginBottom: 2 }}>Booth {group.boothNumber}:</div>
                        <ul style={{ margin: '2px 0 0 18px', padding: 0, lineHeight: 1.3 }}>
                          {group.items.map((item) => (
                            <li key={`required-item-${group.boothNumber}-${item.sequence}-${item.name}`} style={{ marginBottom: 2 }}>
                              {item.name}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <strong>Consent signed:</strong> {consentStatus.signed ? 'Yes' : 'No'}
                <span
                  style={{
                    ...consentMethodTagStyle,
                    padding: '2px 8px',
                    verticalAlign: 'middle',
                  }}
                >
                  Method: {consentMethod}
                </span>
              </div>
              <div style={{ marginBottom: 6 }}><strong>Consent full name:</strong> {consentStatus.fullName || '—'}</div>
              <div style={{ marginBottom: 6 }}><strong>Consent date:</strong> {consentSignedDateStr || '—'}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VendorCheckin;
